import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useEffect, useMemo, useRef, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { useQuery } from "../hooks/useQuery";
import { useStepTestExecutions } from "../hooks/useStepExecutions";
import { stepDirsWithStepTests } from "../lib/stepTestDirs";
import {
  computeStepTestFailures,
  STEP_TEST_TIMEOUT_MS,
  type StepRunStatus,
  stepRunStatus,
  type TestExecution,
} from "../lib/stepTestFailures";
import { makeUrl, routes } from "../lib/urls";
import { Button } from "./Button";
import { Icon } from "./Icon";
import { Link, isInAppNavigation } from "./Link";
import { Modal } from "./Modal";
import { ModalContent, ModalFooter, ModalHead } from "./ModalCard";
import { PreviewListBox } from "./PreviewList";
import { Spinner } from "./Spinner";
import { Text } from "./Text";

const StepTestRow = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  border-radius: 8px;
`;

const RowHead = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NameSlot = styled("div")`
  flex: 1;
  min-width: 0;
`;

const StatusSlot = styled("div")`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const FailedRow = styled(Link)`
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  min-width: 0;

  &:hover {
    background: var(--alpha4);
  }
`;

function StepStatusIcon({ status }: { status: StepRunStatus }) {
  if (status === "running") return <Spinner size={16} />;
  if (status === "passed")
    return <Icon name="check-filled" size={16} color="var(--green-600)" />;
  return <Icon name="check-filled-x" size={16} color="var(--red-600)" />;
}

type Props = {
  workflowId: string;
  branchId: string;
  spaceId: string;
  onProceed: () => void;
  onCancel: () => void;
};

export function PushLivePreflight({
  workflowId,
  branchId,
  spaceId,
  onProceed,
  onCancel,
}: Props) {
  const z = useZero();

  // Read the branch's head commit from the same query the workflow page uses,
  // so the files (and the commit id we run examples against) are the live head.
  const [branch, branchResult] = useQuery(
    queries.loadBranch({ workflowId, branchId })
  );
  const headCommit = branch?.headCommit ?? null;
  const commitId = headCommit?.id ?? null;
  const testStepDirs = useMemo(
    () => stepDirsWithStepTests(headCommit?.files ?? []),
    [headCommit?.files]
  );

  const [runs, setRuns] = useState<
    { stepDir: string; executionId: string }[] | null
  >(null);
  const [timedOut, setTimedOut] = useState(false);
  const startedRef = useRef(false);
  const proceededRef = useRef(false);

  // Once the branch's files have loaded, kick off a fresh step-test run per step
  // that has examples. No examples → nothing to gate, push straight through.
  useEffect(() => {
    if (startedRef.current) return;
    if (branchResult.type !== "complete") return;
    startedRef.current = true;
    if (!commitId || testStepDirs.length === 0) {
      onProceed();
      return;
    }
    setRuns(
      testStepDirs.map((stepDir) => {
        const executionId = nanoid(12);
        z.mutate(
          sharedMutators.runStepTests({
            runId: nanoid(12),
            workflowId,
            commitId,
            stepDir,
            executionId,
          })
        );
        return { stepDir, executionId };
      })
    );
  }, [branchResult.type, testStepDirs, commitId, z, workflowId, onProceed]);

  useEffect(() => {
    if (!runs) return;
    const t = setTimeout(() => setTimedOut(true), STEP_TEST_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [runs]);

  const { executions: testExecutions } = useStepTestExecutions(
    workflowId,
    branchId
  );
  const byId = useMemo(() => {
    const map = new Map<string, TestExecution>();
    for (const e of testExecutions) map.set(e.id, e);
    return map;
  }, [testExecutions]);

  const execs = runs?.map((r) => byId.get(r.executionId)) ?? [];
  const total = runs?.length ?? 0;
  const completed = execs.filter((e) => e && e.status !== "pending").length;
  const allTerminal = runs != null && completed === total;

  // Spec rows commit in the same transaction as the execution's terminal
  // status, so once every run is terminal we can judge immediately.
  const failures =
    runs && (allTerminal || timedOut)
      ? computeStepTestFailures(runs, byId, timedOut)
      : null;

  useEffect(() => {
    if (proceededRef.current) return;
    if (failures && failures.length === 0) {
      proceededRef.current = true;
      onProceed();
    }
  }, [failures, onProceed]);

  return (
    <Modal open onClose={onCancel} alert>
      <ModalContent style={{ width: 320 }}>
        <ModalHead>
          <Text variant="text-regular-md" color="var(--text-primary)">
            Step tests
          </Text>
          <Text variant="text-regular" color="var(--text-secondary)">
            We run these on the latest version before pushing live.
          </Text>
        </ModalHead>
        <PreviewListBox style={{ maxHeight: 280 }}>
          {runs && runs.length > 0 ? (
            runs.map((r) => {
              const status = stepRunStatus(r, byId);
              const rowContent = (
                <>
                  <NameSlot>
                    <Text
                      variant="text-regular"
                      color="var(--text-primary)"
                      ellipsis
                    >
                      {r.stepDir}
                    </Text>
                  </NameSlot>
                  <StatusSlot>
                    <StepStatusIcon status={status} />
                  </StatusSlot>
                </>
              );
              if (status === "failed") {
                const testName =
                  byId
                    .get(r.executionId)
                    ?.testSpecs.find((s) => s.status === "failed")?.name ?? "";
                return (
                  <FailedRow
                    key={r.stepDir}
                    to={makeUrl(routes.space.workflow, {
                      spaceId,
                      workflowId,
                      branchId,
                      step: r.stepDir,
                      stepTest: testName,
                      view: "output",
                    })}
                    onMouseDown={(e) => {
                      if (isInAppNavigation(e)) onCancel();
                    }}
                  >
                    {rowContent}
                  </FailedRow>
                );
              }
              return (
                <StepTestRow key={r.stepDir}>
                  <RowHead>{rowContent}</RowHead>
                </StepTestRow>
              );
            })
          ) : (
            <StepTestRow>
              <RowHead>
                <NameSlot>
                  <Text variant="text-regular" color="var(--text-secondary)">
                    Preparing…
                  </Text>
                </NameSlot>
                <StatusSlot>
                  <Spinner size={16} />
                </StatusSlot>
              </RowHead>
            </StepTestRow>
          )}
        </PreviewListBox>
        <ModalFooter>
          <Button size="l" variant="subtle" onClick={onCancel}>
            Cancel
          </Button>
          {failures != null && failures.length > 0 && (
            <Button size="l" onClick={onProceed}>
              Push live anyway
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
